import { Component, computed, input, output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { HlmButton } from '@spartan-ng/helm/button';
import { ThemedIconComponent } from '../../atoms/themed-icon/themed-icon.component';
import { OCP_ANALYTICS, TrackAction } from 'shared';

@Component({
  selector: 'ocp-pagination',
  imports: [TranslatePipe, HlmButton, ThemedIconComponent],
  templateUrl: './pagination.component.html',
  styleUrl: './pagination.component.scss',
  host: {
    class: 'pagination',
  },
})
export class PaginationComponent {
  readonly OCP_ANALYTICS = OCP_ANALYTICS;
  /** Total number of items/pages. */
  readonly totalPages = input.required<number>();

  /** Zero-based index of the currently active scene. */
  readonly activeIndex = input(0);

  /** Emits the new zero-based index when the user navigates. */
  readonly activeIndexChange = output<number>();

  /** Maximum number of visible page buttons. */
  readonly maxVisiblePages = input(5);

  protected readonly hasPrevious = computed(() => this.activeIndex() > 0);
  protected readonly hasNext = computed(() => this.activeIndex() < this.totalPages() - 1);

  /** Visible page numbers (1-based), capped by maxVisiblePages. */
  protected readonly visiblePages = computed(() => {
    const total = this.totalPages();
    const current = this.activeIndex();
    const max = this.maxVisiblePages();

    if (total <= max) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const half = Math.floor(max / 2);
    let start = current - half + 1;

    if (start < 1) {
      start = 1;
    } else if (start + max - 1 > total) {
      start = total - max + 1;
    }

    return Array.from({ length: max }, (_, i) => start + i);
  });

  @TrackAction(OCP_ANALYTICS.ACTION.CLICK, OCP_ANALYTICS.TOKEN.TASKS_SCENE_PAGINATION_PREVIOUS)
  protected onPrevious(): void {
    const newIndex = this.activeIndex() - 1;
    if (newIndex >= 0) {
      this.activeIndexChange.emit(newIndex);
    }
  }

  @TrackAction(OCP_ANALYTICS.ACTION.CLICK, OCP_ANALYTICS.TOKEN.TASKS_SCENE_PAGINATION_NEXT)
  protected onNext(): void {
    const newIndex = this.activeIndex() + 1;
    if (newIndex < this.totalPages()) {
      this.activeIndexChange.emit(newIndex);
    }
  }

  @TrackAction(OCP_ANALYTICS.ACTION.CLICK, OCP_ANALYTICS.TOKEN.TASKS_SCENE_PAGINATION_PAGE)
  protected onPageClick(page: number): void {
    this.activeIndexChange.emit(page - 1);
  }
}
